import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Catalog = {
  organizations:Array<{organizationId:string;name:string;type:string}>;
  frameworks:Array<{
    frameworkId:string;
    ownerOrganizationId:string;
    ownerName:string;
    code:string;
    name:string;
    versionLabel:string;
    status:string;
    publishedAt?:string|null;
    competencies:Array<{
      competencyId:string;
      code:string;
      name:string;
      description?:string|null;
      orderNo:number;
      indicators:Array<{
        indicatorId:string;
        code:string;
        description:string;
        orderNo:number;
      }>;
    }>;
  }>;
  evaluationDefinitions:Array<{
    definitionId:string;
    ownerOrganizationId:string;
    code:string;
    name:string;
    method:string;
    scoreMin?:number|null;
    scoreMax?:number|null;
    status:string;
    publishedAt?:string|null;
    mappings:Array<{
      mappingId:string;
      competencyId:string;
      competencyName:string;
      frameworkId:string;
      frameworkName:string;
      indicatorId?:string|null;
      indicatorDescription?:string|null;
      weight:number;
    }>;
  }>;
};

function statusLabel(value:string){
  if(value==="DRAFT") return "초안";
  if(value==="PUBLISHED") return "발행";
  if(value==="ARCHIVED") return "보관";
  return value;
}

export function AdminCompetencyStudio({client}:{client:PlatformClient}){
  const [catalog,setCatalog]=useState<Catalog|null>(null);
  const [error,setError]=useState("");
  const [busy,setBusy]=useState("");
  const [refresh,setRefresh]=useState(0);

  const [ownerId,setOwnerId]=useState("");
  const [frameworkCode,setFrameworkCode]=useState("");
  const [frameworkName,setFrameworkName]=useState("");
  const [frameworkVersion,setFrameworkVersion]=useState("1.0");
  const [selectedFrameworkId,setSelectedFrameworkId]=useState("");
  const [competencyCode,setCompetencyCode]=useState("");
  const [competencyName,setCompetencyName]=useState("");
  const [selectedCompetencyId,setSelectedCompetencyId]=useState("");
  const [indicatorCode,setIndicatorCode]=useState("");
  const [indicatorDescription,setIndicatorDescription]=useState("");

  const [evalCode,setEvalCode]=useState("");
  const [evalName,setEvalName]=useState("");
  const [selectedEvalId,setSelectedEvalId]=useState("");
  const [mappingCompetencyId,setMappingCompetencyId]=useState("");
  const [mappingIndicatorId,setMappingIndicatorId]=useState("");

  useEffect(()=>{
    let active=true;
    client.rpc("admin_competency_catalog").then(({data,error})=>{
      if(!active) return;
      if(error){
        setError(error.message);
        return;
      }
      const next=data as Catalog;
      setCatalog(next);
      setOwnerId((value)=>value || next.organizations[0]?.organizationId || "");
      setSelectedFrameworkId((value)=>
        next.frameworks.some((item)=>item.frameworkId===value)
          ? value
          : next.frameworks[0]?.frameworkId || ""
      );
      setSelectedEvalId((value)=>
        next.evaluationDefinitions.some((item)=>item.definitionId===value)
          ? value
          : next.evaluationDefinitions.find((item)=>item.status==="DRAFT")?.definitionId
            || next.evaluationDefinitions[0]?.definitionId
            || ""
      );
    });
    return()=>{active=false;};
  },[client,refresh]);

  const selectedFramework=useMemo(
    ()=>catalog?.frameworks.find((item)=>item.frameworkId===selectedFrameworkId) || null,
    [catalog,selectedFrameworkId]
  );

  useEffect(()=>{
    if(!selectedFramework){
      setSelectedCompetencyId("");
      return;
    }
    setSelectedCompetencyId((value)=>
      selectedFramework.competencies.some((item)=>item.competencyId===value)
        ? value
        : selectedFramework.competencies[0]?.competencyId || ""
    );
  },[selectedFramework]);

  const selectedCompetency=useMemo(
    ()=>selectedFramework?.competencies.find((item)=>item.competencyId===selectedCompetencyId) || null,
    [selectedFramework,selectedCompetencyId]
  );

  const selectedEval=useMemo(
    ()=>catalog?.evaluationDefinitions.find((item)=>item.definitionId===selectedEvalId) || null,
    [catalog,selectedEvalId]
  );

  const publishedCompetencies=useMemo(
    ()=>catalog?.frameworks
      .filter((framework)=>framework.status==="PUBLISHED")
      .flatMap((framework)=>framework.competencies.map((competency)=>({
        ...competency,
        frameworkId:framework.frameworkId,
        frameworkName:framework.name
      }))) || [],
    [catalog]
  );

  const mappingCompetency=useMemo(
    ()=>publishedCompetencies.find((item)=>item.competencyId===mappingCompetencyId) || null,
    [publishedCompetencies,mappingCompetencyId]
  );

  useEffect(()=>{
    if(publishedCompetencies.length && !publishedCompetencies.some((item)=>item.competencyId===mappingCompetencyId)){
      setMappingCompetencyId(publishedCompetencies[0].competencyId);
    }
  },[publishedCompetencies,mappingCompetencyId]);

  useEffect(()=>{
    if(!mappingCompetency){
      setMappingIndicatorId("");
      return;
    }
    if(!mappingCompetency.indicators.some((item)=>item.indicatorId===mappingIndicatorId)){
      setMappingIndicatorId(mappingCompetency.indicators[0]?.indicatorId || "");
    }
  },[mappingCompetency,mappingIndicatorId]);

  async function run(key:string, action:()=>PromiseLike<{error?:{message:string}|null}>){
    setBusy(key);
    setError("");
    try{
      const result=await action();
      if(result.error) throw new Error(result.error.message);
      setRefresh((value)=>value+1);
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
    }finally{
      setBusy("");
    }
  }

  if(!catalog) return <p className="muted">역량 프레임워크를 불러오는 중…</p>;

  return (
    <section className="admin-competency-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Result → Competency</p>
          <h2>역량 프레임워크</h2>
          <p className="route-description">발행된 역량 기준을 평가 정의와 연결해 평가 확정 시 CompetencyResult를 자동 생성합니다.</p>
        </div>
        <span className="phase-badge">BUILD-06</span>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="admin-studio-grid">
        <section className="studio-card">
          <h3>새 역량 프레임워크</h3>
          <div className="studio-form">
            <label>소유기관
              <select value={ownerId} onChange={(event)=>setOwnerId(event.target.value)}>
                {catalog.organizations.map((item)=><option key={item.organizationId} value={item.organizationId}>{item.name}</option>)}
              </select>
            </label>
            <label>코드<input value={frameworkCode} onChange={(event)=>setFrameworkCode(event.target.value)} placeholder="CAREER_CORE" /></label>
            <label>이름<input value={frameworkName} onChange={(event)=>setFrameworkName(event.target.value)} placeholder="진로 핵심역량" /></label>
            <label>버전<input value={frameworkVersion} onChange={(event)=>setFrameworkVersion(event.target.value)} placeholder="1.0" /></label>
            <button className="primary compact-button" disabled={busy!=="" || !ownerId || !frameworkCode.trim() || !frameworkName.trim()}
              onClick={()=>run("framework-create",()=>client.rpc("admin_create_competency_framework",{
                p_owner_organization_id:ownerId,
                p_code:frameworkCode,
                p_name:frameworkName,
                p_version_label:frameworkVersion
              }))}>
              {busy==="framework-create" ? "생성 중…" : "프레임워크 생성"}
            </button>
          </div>
        </section>

        <section className="studio-card">
          <h3>평가 기준 생성</h3>
          <div className="studio-form">
            <label>소유기관
              <select value={ownerId} onChange={(event)=>setOwnerId(event.target.value)}>
                {catalog.organizations.map((item)=><option key={item.organizationId} value={item.organizationId}>{item.name}</option>)}
              </select>
            </label>
            <label>코드<input value={evalCode} onChange={(event)=>setEvalCode(event.target.value)} placeholder="PROJECT-EVAL-V1" /></label>
            <label>이름<input value={evalName} onChange={(event)=>setEvalName(event.target.value)} placeholder="프로젝트 수행 평가" /></label>
            <button className="primary compact-button" disabled={busy!=="" || !ownerId || !evalCode.trim() || !evalName.trim()}
              onClick={()=>run("eval-create",()=>client.rpc("admin_create_numeric_evaluation_definition",{
                p_owner_organization_id:ownerId,
                p_code:evalCode,
                p_name:evalName,
                p_score_min:0,
                p_score_max:100
              }))}>
              {busy==="eval-create" ? "생성 중…" : "0~100 평가 기준 생성"}
            </button>
          </div>
        </section>
      </div>

      <div className="competency-workspace">
        <aside className="studio-list">
          <div className="panel-title"><strong>프레임워크</strong><span>{catalog.frameworks.length}개</span></div>
          {catalog.frameworks.map((item)=>(
            <button key={item.frameworkId} className={selectedFrameworkId===item.frameworkId ? "studio-list-row active" : "studio-list-row"}
              onClick={()=>setSelectedFrameworkId(item.frameworkId)}>
              <strong>{item.name}</strong>
              <span>{item.versionLabel} · {statusLabel(item.status)}</span>
            </button>
          ))}
        </aside>

        <div className="studio-detail">
          {selectedFramework ? (
            <>
              <header className="studio-detail-head">
                <div>
                  <p className="eyebrow">{selectedFramework.code}</p>
                  <h2>{selectedFramework.name}</h2>
                  <p>{selectedFramework.ownerName} · v{selectedFramework.versionLabel}</p>
                </div>
                <span className={selectedFramework.status==="PUBLISHED" ? "status-pill good" : "status-pill"}>{statusLabel(selectedFramework.status)}</span>
              </header>

              {selectedFramework.status==="DRAFT" && (
                <div className="studio-editor">
                  <div className="studio-form inline">
                    <label>역량 코드<input value={competencyCode} onChange={(event)=>setCompetencyCode(event.target.value)} /></label>
                    <label>역량 이름<input value={competencyName} onChange={(event)=>setCompetencyName(event.target.value)} /></label>
                    <button className="secondary" disabled={busy!=="" || !competencyCode.trim() || !competencyName.trim()}
                      onClick={()=>run("competency-add",()=>client.rpc("admin_add_competency",{
                        p_framework_id:selectedFramework.frameworkId,
                        p_code:competencyCode,
                        p_name:competencyName,
                        p_description:null
                      }))}>역량 추가</button>
                  </div>

                  {selectedFramework.competencies.length>0 && (
                    <div className="studio-form inline">
                      <label>대상 역량
                        <select value={selectedCompetencyId} onChange={(event)=>setSelectedCompetencyId(event.target.value)}>
                          {selectedFramework.competencies.map((item)=><option key={item.competencyId} value={item.competencyId}>{item.name}</option>)}
                        </select>
                      </label>
                      <label>지표 코드<input value={indicatorCode} onChange={(event)=>setIndicatorCode(event.target.value)} /></label>
                      <label>지표 설명<input value={indicatorDescription} onChange={(event)=>setIndicatorDescription(event.target.value)} /></label>
                      <button className="secondary" disabled={busy!=="" || !selectedCompetencyId || !indicatorCode.trim() || !indicatorDescription.trim()}
                        onClick={()=>run("indicator-add",()=>client.rpc("admin_add_competency_indicator",{
                          p_competency_id:selectedCompetencyId,
                          p_code:indicatorCode,
                          p_description:indicatorDescription
                        }))}>지표 추가</button>
                    </div>
                  )}

                  <button className="primary compact-button" disabled={busy!=="" || selectedFramework.competencies.length===0}
                    onClick={()=>run("framework-publish",()=>client.rpc("admin_publish_competency_framework",{
                      p_framework_id:selectedFramework.frameworkId
                    }))}>프레임워크 발행·동결</button>
                </div>
              )}

              <div className="competency-tree">
                {selectedFramework.competencies.map((item)=>(
                  <div className="competency-node" key={item.competencyId}>
                    <strong>{item.name}</strong>
                    <span>{item.code}</span>
                    {item.indicators.map((indicator)=><small key={indicator.indicatorId}>• {indicator.description}</small>)}
                  </div>
                ))}
                {!selectedFramework.competencies.length && <p className="case-empty">등록된 역량이 없습니다.</p>}
              </div>
            </>
          ):<p className="case-empty">프레임워크를 생성하거나 선택하세요.</p>}
        </div>
      </div>

      <section className="studio-card evaluation-mapping-card">
        <div className="panel-title"><strong>Evaluation → Competency Mapping</strong><span>발행 후 수정 불가</span></div>
        <div className="mapping-grid">
          <label>평가 정의
            <select value={selectedEvalId} onChange={(event)=>setSelectedEvalId(event.target.value)}>
              {catalog.evaluationDefinitions.map((item)=><option key={item.definitionId} value={item.definitionId}>{item.name} · {statusLabel(item.status)}</option>)}
            </select>
          </label>
          <label>역량
            <select value={mappingCompetencyId} onChange={(event)=>setMappingCompetencyId(event.target.value)}>
              {publishedCompetencies.map((item)=><option key={item.competencyId} value={item.competencyId}>{item.frameworkName} / {item.name}</option>)}
            </select>
          </label>
          <label>지표
            <select value={mappingIndicatorId} onChange={(event)=>setMappingIndicatorId(event.target.value)}>
              <option value="">역량 전체</option>
              {mappingCompetency?.indicators.map((item)=><option key={item.indicatorId} value={item.indicatorId}>{item.description}</option>)}
            </select>
          </label>
        </div>

        {selectedEval && (
          <>
            <div className="mapping-actions">
              {selectedEval.status==="DRAFT" ? (
                <>
                  <button className="secondary" disabled={busy!=="" || !mappingCompetencyId}
                    onClick={()=>run("mapping-add",()=>client.rpc("admin_map_evaluation_competency",{
                      p_evaluation_definition_id:selectedEval.definitionId,
                      p_competency_id:mappingCompetencyId,
                      p_indicator_id:mappingIndicatorId || null,
                      p_weight:1
                    }))}>역량 매핑 추가</button>
                  <button className="primary compact-button" disabled={busy!==""}
                    onClick={()=>run("evaluation-publish",()=>client.rpc("admin_publish_evaluation_definition",{
                      p_definition_id:selectedEval.definitionId
                    }))}>평가 정의 발행·동결</button>
                </>
              ):<span className="status-pill good">발행본 · 불변</span>}
            </div>
            <div className="mapping-list">
              {selectedEval.mappings.map((item)=>(
                <div className="review-line" key={item.mappingId}>
                  <div><strong>{item.competencyName}</strong><span>{item.frameworkName}{item.indicatorDescription ? ` · ${item.indicatorDescription}` : ""}</span></div>
                  <small>가중치 {item.weight}</small>
                </div>
              ))}
              {!selectedEval.mappings.length && <p className="case-empty">아직 연결된 역량이 없습니다.</p>}
            </div>
          </>
        )}
      </section>
    </section>
  );
}
